import { Box, BoxProps, Flex, HStack, Spacer, Text } from "@chakra-ui/react";
import { Dispatch, SetStateAction } from "react";
import { ActionLabel } from "../../../../components/actionLabel/ActionLabel";
import { SectionBox } from "../../../../components/sectionBox/SectionBox";
import {
  CampaignType,
  DspChoices,
  LineTypeChoicesEnum,
  SegmentsBundleContextLineTypeChoices,
} from "../../../../graphql/generated";
import {
  formatTextDisplay,
  getCostName,
  getLookbackName,
  LookbackOptionsEnum,
  lookbackOptionsType,
} from "../../../../utils/displayGetters";
import React from "react";
import { SelectFieldV2 } from "../../../../components/selectField/SelectFieldV2";

interface OptimusChartBoxProps extends BoxProps {
  chartBoxHeader?: string;
  lookback?: {
    selectLookback: lookbackOptionsType;
    setSelectLookback: Dispatch<SetStateAction<lookbackOptionsType>>;
  };
  campaign?: {
    selectCampaign: campaignOptionsType;
    setSelectCampaign: Dispatch<SetStateAction<campaignOptionsType>>;
  };
  allCampaigns?: CampaignType[];
  campaignsWithContext?: any[];
  tacticsList?: LineTypeChoicesEnum[];
  tacticName?: {
    selectTacticName: tacticNameType[];
    setTacticName: Dispatch<SetStateAction<tacticNameType[]>>;
  };
  tactic?: {
    selectTactic: tacticOptionsType[];
    setSelectTactic: Dispatch<SetStateAction<tacticOptionsType[]>>;
  };
  kpi?: {
    selectKpi: kpiOptionsType[];
    setSelectKpi: Dispatch<SetStateAction<kpiOptionsType[]>>;
  };
  costMetric?: {
    selectCostMetric: costMetricOptionsType;
    setSelectCostMetric: Dispatch<SetStateAction<costMetricOptionsType>>;
  };
}

export interface dspFilterOptionType {
  label: string;
  value: DspChoices | "ALL";
}



export interface campaignOptionsType {
  label: string;
  value: string;
  relevantIdList: {
    lineType: SegmentsBundleContextLineTypeChoices[];
    relevantIdList: string[];
  }[];
  lineType: SegmentsBundleContextLineTypeChoices[];
  dsp: DspChoices;
}

export interface tacticNameType {
  label: string;
  value: LineTypeChoicesEnum;
}

export interface tacticOptionsType {
  label: string;
  value: number | string;
}

export interface kpiOptionsType {
  label: string;
  value: string;
}

export interface costMetricOptionsType {
  label: string;
  value: CostOptionsEnum;
}


export enum kpiChoicesEnum {
  Cpa = "CPA",
  Cpc = "CPC",
  Cpv = "CPV",
  Ctr = "CTR",
  Cpm = "CPM",
  impressions = "Impressions",
  Vcr = "VCR",
  Viewability = "Viewability",
  MediaSpend = "Media Spend",
}

export enum CostOptionsEnum {
  media_cost_adv_currency = "media_cost_adv_currency",
  media_cost_partner_currency = "media_cost_partner_currency",
  media_cost_usd = "media_cost_usd",
  advertiser_cost_adv_currency = "advertiser_cost_adv_currency",
  advertiser_cost_partner_currency = "advertiser_cost_partner_currency",
  advertiser_cost_usd = "advertiser_cost_usd",
  revenue_advertiser_currency = "revenue_advertiser_currency",
  revenue_partner_currency = "revenue_partner_currency",
  revenue_usd = "revenue_usd",
}

export const OptimusChartBox = ({
  chartBoxHeader,
  children,
  lookback,
  campaign,
  allCampaigns,
  tacticName,
  tactic,
  tacticsList,
  kpi,
  campaignsWithContext,
  costMetric,
  ...rest
}: OptimusChartBoxProps) => {
  const kpiSelectOptions: kpiOptionsType[] = [
    ...Object.entries(kpiChoicesEnum).map(([, value]) => ({
      label: String(value),
      value: value,
    })),
  ];

  const lookbackOptions: lookbackOptionsType[] = [
    ...Object.entries(LookbackOptionsEnum).map(([, value]) => ({
      label: getLookbackName(value),
      value: value,
    })),
  ];

  const costMetricOptions: costMetricOptionsType[] = [
    ...Object.entries(CostOptionsEnum).map(([, value]) => ({
      label: getCostName(value),
      value: value,
    })),
  ];

  let campaignsWithContextList: campaignOptionsType[] = [];

  if (campaignsWithContext) {
    campaignsWithContextList = [
      ...campaignsWithContext.map((campaign: campaignOptionsType) => ({
        label: campaign.label,
        value: campaign.value,
        relevantIdList: campaign.relevantIdList,
        lineType: campaign.lineType,
        dsp: campaign.dsp,
      })),
    ];
  }

  // list out adgroup/line item ID's for filter
  const tacticIdOptionsList: tacticOptionsType[] = [];
  if (
    campaign &&
    campaign.selectCampaign.label !== "default" &&
    campaign.selectCampaign.relevantIdList &&
    tacticName?.selectTacticName
  ) {
    campaign.selectCampaign.relevantIdList.map((ids: any) => {
      if (
        tacticName.selectTacticName[0]?.value &&
        tacticName.selectTacticName[0].value === ids.lineType[0]
      ) {
        ids.relevantIdList.forEach((id: string) => {
          tacticIdOptionsList.push({
            label: String(id),
            value: String(id),
          
        });
      })
      }
    });
  }

  // list out tactic names for filter
  let tacticNameList: tacticNameType[] = [];
  if (campaign && campaign.selectCampaign.lineType) {
    tacticNameList = [
      ...(campaign?.selectCampaign.lineType).map((value: any) => ({
        label: formatTextDisplay(value),
        value: value,
      })),
    ];
  }

  return (
    <SectionBox p={2} m={4} pl={30} {...rest}>
      <Text px={4} fontWeight={"bold"} transform="translateX(20px)" {...rest}>
        {chartBoxHeader}
      </Text>
      <HStack
        pt={1}
        pb={4}
        mx={2}
        spacing={1}
        {...rest}
        justifyContent={"flex-end"}
      >
        <Spacer />
        {campaign && (
          <ActionLabel label={""}>
            <Box minW={180} justifyContent={"flex-end"}>
              <SelectFieldV2
                value={campaign.selectCampaign}
                isMulti={false}
                options={campaignsWithContextList}
                onChange={(option) => {
                  campaign.setSelectCampaign(option);
                }}
                menuWidth={"auto"}
              />
            </Box>
          </ActionLabel>
        )}
        {lookback && (
          <ActionLabel label={""}>
            <Box minW={180} justifyContent={"flex-end"}>
              <SelectFieldV2
                value={lookback.selectLookback}
                isMulti={false}
                options={lookbackOptions}
                onChange={(option) => {
                  lookback.setSelectLookback(option);
                }}
              />
            </Box>
          </ActionLabel>
        )}
        {tacticName && (
          <ActionLabel label={""}>
            <Box minW={180} justifyContent={"flex-end"}>
              <SelectFieldV2
                value={tacticName.selectTacticName}
                placeholder="Select Line Type"
                isMulti={false}
                options={tacticNameList}
                onChange={(option) => {
                  const newOption = Array.isArray(option) ? option : [option];
                  tacticName?.setTacticName(newOption);
                }}
                isOptionDisabled={() => false}
              />
            </Box>
          </ActionLabel>
        )}
        {tactic && (
          <ActionLabel label={""}>
            <Box minW={180} justifyContent={"flex-end"}>
              <SelectFieldV2
                value={tactic.selectTactic}
                isMulti={true}
                placeholder="Select Tactic ID"
                options={tacticIdOptionsList}
                onChange={(option) => {
                  tactic.setSelectTactic(option);
                }}
                isOptionDisabled={() => false}
              />
            </Box>
          </ActionLabel>
        )}
        {kpi && (
          <ActionLabel label={""}>
            <Box minW={180} justifyContent={"flex-end"}>
              <SelectFieldV2
                value={kpi.selectKpi}
                isMulti={true}
                options={kpiSelectOptions}
                onChange={(option) => {
                  kpi.setSelectKpi(option);
                }}
                isOptionDisabled={() => kpi.selectKpi.length >= 2}
              />
            </Box>
          </ActionLabel>
        )}
        {costMetric && (
          <ActionLabel label={""}>
            <Box minW={180} justifyContent={"flex-end"}>
              <SelectFieldV2
                value={costMetric.selectCostMetric}
                isMulti={false}
                options={costMetricOptions}
                onChange={(option) => {
                  costMetric.setSelectCostMetric(option);
                }}
                menuWidth={"auto"}
              />
            </Box>
          </ActionLabel>
        )}
      </HStack>
      {children}
      <Flex flexDir={"row"}></Flex>
    </SectionBox>
  );
};
